/**
 * MobileTopBar — the top band a phone gets where the navbar is hidden.
 *
 * THE DEFECT THIS CLOSES (obs. 7). `PageShell` composed exactly three bands:
 * the navbar (`hidden md:block`), the footer (`hidden md:block`) and the tab
 * bar (`md:hidden`). Below `md` that leaves a `focus` or `article` page — a
 * listing detail, an estate, a district, settings, support, a conversation
 * thread, a blog post — with NO top band at all and no in-page way back:
 * measured on the hermetic fleet at 393x852, the listing detail rendered zero
 * visible `header` elements and zero back controls. The only mobile top bar in
 * the app was the browse hub's own `ListingsTopBar`, whose back control always
 * went to the locale home whatever the reader had come from.
 *
 * The remedy is not a header component each page can remember to render. It is
 * one more COLUMN of the chrome table (`ChromeSpec.mobileHeader`), so the band
 * is a property of the kind a route declares, rendered here once. A page
 * module could not mount it anyway: `page` may not import `infra` in the
 * element DAG, which is exactly why the shell owns it.
 *
 * ## What the band carries, and what it used to (R4-4)
 *
 * The back control and THE PAGE — its title, left-aligned beside the control,
 * with an optional second line under it. Until round 4 it carried the brand MARK
 * instead and had no title channel at all, which is what the owner reported and
 * what the round-4 probe measured: on a listing detail at 393x852 the band's
 * grid was `40px 281px 40px`, its text content was the EMPTY STRING, and the
 * page beneath it had an `h1` of `$28,000`. A reader who deep-linked in was
 * told the company's name and nothing about where they were.
 *
 * The mark belongs to the ROOT surfaces, where the navbar and the tab bar
 * already carry it; a drilled-into page's band shows the page, on iOS (title,
 * large then small), on Material (title beside the navigation icon), and in the
 * Rails `Ui::MobileTopBarComponent` this app replaced — whose `leading` /
 * `title` / `subtitle` / `trailing` slots rendered the title left-aligned in
 * exactly this position.
 *
 * The band takes TEXT and knows nothing about where it came from. The two
 * sources are different types — a manifest `title:` is a catalog KEY, a
 * loader's `bandTitle` is backend-localised text — and `PageShell` resolves
 * both, so there is one resolution point and no second rendering path.
 *
 * `sticky top-0`, so it stays in FLOW: it needs no spacer the way the `fixed`
 * tab bar needs `pb-16`, which is why `ChromeSpec` has no `pt-*` twin of that
 * derivation. `Z_LAYERS.navbar` is the same page layer the desktop navbar
 * takes — it is the same band at the same edge, one breakpoint apart.
 */
import { BackLink } from '@/shared/components/BackLink';
import type { Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

export interface MobileTopBarProps {
  /** Where a COLD back goes — the surface's declared logical parent. */
  readonly backTo: Route;
  /**
   * What this page IS, already localised — the manifest's `title:` key rendered
   * by the shell, or the loader's `bandTitle`. `LocalizedText` and not `string`,
   * so a hand-typed English literal here is a compile error.
   */
  readonly title: LocalizedText;
  /** An optional second line: the address, the listing, the mode. */
  readonly subtitle?: LocalizedText;
}

export function MobileTopBar({ backTo, title, subtitle }: MobileTopBarProps) {
  return (
    <header
      data-slot="mobile-top-bar"
      className={cn(
        'sticky top-0 md:hidden',
        Z_LAYERS.navbar.className,
        'h-[var(--mobile-header-height)] border-b border-border bg-background'
      )}
    >
      {/* `minmax(0,1fr)` and not `1fr`: a grid track's default `min-width:auto`
          is its content's, so a long estate name would push the counterweight
          cell off the band instead of truncating inside its own. */}
      <div className="grid h-full grid-cols-[40px_minmax(0,1fr)_40px] items-center gap-2 px-2">
        <BackLink backTo={backTo} />
        <div className="flex min-w-0 flex-col justify-center text-left">
          <span className="truncate text-base font-semibold text-foreground">{title}</span>
          {subtitle !== undefined && (
            <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
          )}
        </div>
        {/* The optical counterweight to the back control, kept now that the
            centre cell is left-aligned: Rails carried `trailing` actions (share,
            save) here and this row is where they will land. `aria-hidden`
            because it carries nothing — a reader hears one control here. */}
        <div aria-hidden="true" />
      </div>
    </header>
  );
}
